import React from "react";
import { useWorldToScreen } from "../hooks/useWorldToScreen";

interface ScoreLabelProps {
  world: { x: number; y: number };
  text: string;
  className?: string;
  style?: React.CSSProperties;
}

export const ScoreLabel: React.FC<ScoreLabelProps> = ({
  world,
  text,
  className,
  style,
}) => {
  const screenPos = useWorldToScreen(world);

  const labelStyle: React.CSSProperties = {
    position: "absolute",
    left: screenPos.left,
    top: screenPos.top,
    fontFamily: '"Roboto", sans-serif',
    fontSize: "20px",
    color: "white",
    pointerEvents: "none",
    userSelect: "none",
    whiteSpace: "nowrap",
    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)", // Add shadow for better visibility
    transform: "translate(-50%, -50%)", // Center the text on the world coordinate
    zIndex: 1001,
    ...style,
  };

  return (
    <div className={className} style={labelStyle}>
      {text}
    </div>
  );
};

// Enhanced version with animation support
interface AnimatedScoreLabelProps extends ScoreLabelProps {
  animate?: boolean;
  animationDuration?: number;
}

export const AnimatedScoreLabel: React.FC<AnimatedScoreLabelProps> = ({
  world,
  text,
  className,
  style,
  animate = false,
  animationDuration = 1000,
}) => {
  const screenPos = useWorldToScreen(world);

  const labelStyle: React.CSSProperties = {
    position: "absolute",
    left: screenPos.left,
    top: screenPos.top,
    fontFamily: '"Roboto", sans-serif',
    fontSize: "20px",
    color: "white",
    pointerEvents: "none",
    userSelect: "none",
    whiteSpace: "nowrap",
    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
    transform: "translate(-50%, -50%)",
    zIndex: 1001,
    transition: animate ? `all ${animationDuration}ms ease-out` : "none",
    ...style,
  };

  return (
    <div className={className} style={labelStyle}>
      {text}
    </div>
  );
};

// Multi-line text support
interface MultiLineScoreLabelProps {
  world: { x: number; y: number };
  lines: string[];
  className?: string;
  style?: React.CSSProperties;
  lineHeight?: number;
}

export const MultiLineScoreLabel: React.FC<MultiLineScoreLabelProps> = ({
  world,
  lines,
  className,
  style,
  lineHeight = 24,
}) => {
  const screenPos = useWorldToScreen(world);

  const containerStyle: React.CSSProperties = {
    position: "absolute",
    left: screenPos.left,
    top: screenPos.top,
    fontFamily: '"Roboto", sans-serif',
    fontSize: "20px",
    color: "white",
    pointerEvents: "none",
    userSelect: "none",
    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.8)",
    transform: "translate(-50%, -50%)",
    zIndex: 1001,
    textAlign: "center",
    ...style,
  };

  return (
    <div className={className} style={containerStyle}>
      {lines.map((line, index) => (
        <div
          key={index}
          style={{
            lineHeight: `${lineHeight}px`,
            whiteSpace: "nowrap",
          }}
        >
          {line}
        </div>
      ))}
    </div>
  );
};
